<script setup lang="ts">
import { SearchIcon } from '@/components/common/icon/Search'
import RlErrorAlert from '@/components/common/RlErrorAlert.vue'
import { useSpareParts } from '@/composables/rc/spare-parts'
import type { ISparePartsDashboardFilters } from '@/types/rc/filters'
import { type PartType } from '@/types/rc/miners/PartType'
import { SparePartStatus } from '@/types/rc/spare-parts'
import { OrderBy, type IFragmentFilters } from '@/types/shared/filters'
import { generateModelDisplayName } from '@/util/rc'
import { computed, reactive, ref } from 'vue'

const props = defineProps<{
  partType: PartType
  customerId: number
}>()

// Container select refs
const activeTabName = ref<string>('company')
const selectedSparePart = defineModel<{ id: number }>({ required: false })

const filters = reactive<IFragmentFilters>({
  page: 1,
  per_page: 10,
  fragment: undefined,
  order_by: 'id',
  order_by_order: OrderBy.ASC,
})

const computedFilters = computed<ISparePartsDashboardFilters>(() => ({
  ...filters,
  part_type: props.partType.valueOf(),
  status: [SparePartStatus.InStock],
  ...(activeTabName.value === 'customer' ? { customer_id: props.customerId } : { company_owned: true }),
}))

const { getSparesQuery } = useSpareParts()
const {
  isFetching: sparePartsIsFetching,
  data: spareParts,
  isError,
  error,
  refetch,
  isRefetching: sparePartsIsRefetching,
} = getSparesQuery(computedFilters)

const formatSparePartName = (sparePart: any): string => {
  return sparePart.part_sn ? `ID #${sparePart.id} - ${sparePart.part_sn}` : `ID #${sparePart.id}`
}

/**
 * Generate part options, group by model and model version.
 */
const partOptions = computed(() => {
  const result =
    spareParts.value?.data?.map((sparePart: any) => ({
      label: generateModelDisplayName(sparePart.part_model, sparePart?.part_model_version),
      value: sparePart,
    })) ?? []

  return Array.from(
    result.reduce((acc: Map<string, any>, { label, value }) => {
      const customLabel = formatSparePartName(value)

      const group = acc.get(label) || { label, value: label, options: [] }
      group.options.push({ label: customLabel, value })
      return acc.set(label, group) // returns new Map with updated key (Map is immutable)
    }, new Map<string, any>())
  ).map(([, group]) => group)
})
</script>

<template>
  <el-select-v2
    v-model="selectedSparePart"
    title="Replaced with"
    :options="partOptions"
    value-key="id"
    clearable
    :loading="sparePartsIsFetching"
    @visible-change="(isVisible: boolean) => !isVisible && (activeTabName = 'company')"
  >
    <template #label="{ value }">
      <span>{{ value.part_model }}</span
      >&nbsp;
      <span>({{ formatSparePartName(value) }})</span>
    </template>
    <template #header>
      <el-tabs v-model="activeTabName" @tab-change="selectedSparePart = undefined">
        <el-tab-pane label="Owned by us" name="company" />
        <el-tab-pane label="Owned by Customer" name="customer" />
      </el-tabs>
      <el-input v-model="filters.fragment" :prefix-icon="SearchIcon" placeholder="Search" clearable />
    </template>
    <template v-if="isError && !sparePartsIsRefetching" #footer>
      <rl-error-alert :error="error" close-text="Retry?" @close="refetch" />
    </template>
  </el-select-v2>
</template>

<style scoped lang="scss">
.scrollable-list {
  max-height: 132px;
  overflow-y: auto;
}

ul {
  list-style-type: disc;
  list-style-position: inside;
}
</style>
